import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Box, Button, Chip, CircularProgress, Link, Snackbar, Typography } from '@material-ui/core'
import MuiAlert from '@material-ui/lab/Alert'
import axios from 'axios'
import {
  applyBasemapProvider,
  getLatestMaricopaProvider,
  googleHybridProvider,
} from '../v3/maricopaBasemap'

import DrawZoneModal from '../v3/DrawZoneModal'

const SPRAY_TYPE_COLORS = {
  turf: { fill: '#00FF00', stroke: '#00AA00' },
  ornamental: { fill: '#FFFF00', stroke: '#CCAA00' },
  bare_ground: { fill: '#FFA500', stroke: '#CC7700' },
  pre_emergent: { fill: '#66BB6A', stroke: '#388E3C' },
  no_spray: { fill: '#9e9e9e', stroke: '#777777' },
  none: { fill: '#9e9e9e', stroke: '#777777' }
}

const SPRAY_TYPE_LABELS = {
  turf: 'Turf',
  ornamental: 'Ornamental',
  bare_ground: 'Bare Ground',
  pre_emergent: 'Pre-Emergent',
  no_spray: 'No Spray',
  none: 'Other',
  other: 'Other'
}

const normalizePoint = (point = {}) => {
  const lat = Number(point.lat ?? point.latitude)
  const lng = Number(point.lng ?? point.lon ?? point.longitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

const normalizePaths = (paths = []) => paths
  .map(normalizePoint)
  .filter(Boolean)

const hasCustomOnlyScope = (sprayScope = {}) => (
  (sprayScope.mode === 'all') &&
  Array.isArray(sprayScope.customZones) &&
  sprayScope.customZones.length > 0 &&
  !(sprayScope.selectedAreaIds || []).length
)

const getInitialMode = (sprayScope = {}) => hasCustomOnlyScope(sprayScope)
  ? 'selected'
  : (sprayScope.mode || 'all')

const isSprayableArea = (area = {}) => area.hasPreEmergent === true

const SprayScopeSection = ({ url, scheduleItemId, jobsiteId, jobsiteLocation, sprayScope, onScopeChange }) => {
  const [areas, setAreas] = useState([])
  const [propertyLines, setPropertyLines] = useState([])
  const [slices, setSlices] = useState([])
  const [clearingSlices, setClearingSlices] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [drawModalOpen, setDrawModalOpen] = useState(false)
  const [mapContainerNode, setMapContainerNode] = useState(null)

  // Leaflet refs
  const mapContainerRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const tileLayerRef = useRef(null)
  const polygonLayersRef = useRef([])

  // Local selection state
  const [mode, setMode] = useState(getInitialMode(sprayScope))
  const [selectedIds, setSelectedIds] = useState(new Set(sprayScope?.selectedAreaIds || []))
  const [customZones, setCustomZones] = useState(sprayScope?.customZones || [])
  const [dirty, setDirty] = useState(false)
  const [externalScopeUpdate, setExternalScopeUpdate] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  // Dirty-guard refs
  const prevScheduleItemIdRef = useRef(scheduleItemId)
  const spraySnapshotRef = useRef(null)

  // Sync local state when sprayScope prop changes (e.g., different item selected)
  useEffect(() => {
    const snapshotScope = (scope) => ({
      mode: scope?.mode || 'all',
      selectedAreaIds: JSON.stringify((scope?.selectedAreaIds || []).slice().sort()),
      customZonesCount: (scope?.customZones || []).length
    })

    // Different item — full reset
    if (scheduleItemId !== prevScheduleItemIdRef.current) {
      prevScheduleItemIdRef.current = scheduleItemId
      spraySnapshotRef.current = snapshotScope(sprayScope)
      setMode(getInitialMode(sprayScope))
      setSelectedIds(new Set((sprayScope?.selectedAreaIds || []).map(String)))
      setCustomZones(sprayScope?.customZones || [])
      setDirty(false)
      setExternalScopeUpdate(false)
      return
    }

    // Same item, not dirty — apply update
    if (!dirty) {
      spraySnapshotRef.current = snapshotScope(sprayScope)
      setMode(getInitialMode(sprayScope))
      setSelectedIds(new Set((sprayScope?.selectedAreaIds || []).map(String)))
      setCustomZones(sprayScope?.customZones || [])
      return
    }

    // Same item, dirty — check if server scope actually changed
    const prev = spraySnapshotRef.current
    const curr = snapshotScope(sprayScope)
    if (prev && (prev.mode !== curr.mode || prev.selectedAreaIds !== curr.selectedAreaIds || prev.customZonesCount !== curr.customZonesCount)) {
      spraySnapshotRef.current = curr
      setExternalScopeUpdate(true)
    }
    // If server unchanged, skip — preserve user's selections
  }, [scheduleItemId, sprayScope])

  const fetchAreas = useCallback(async () => {
    if (!scheduleItemId) return
    setLoading(true)
    try {
      const params = jobsiteId ? { jobsiteId } : {}
      const resp = await axios.get(`${url}/api/herbicide-schedule/${scheduleItemId}/areas`, { params })
      // Server already filters out exclusion zones (building footprints, etc.)
      const fetched = resp.data.areas || []
      setAreas(fetched)
      setPropertyLines(resp.data.propertyLines || [])
      setSlices(resp.data.slices || [])

      // If no prior scope is saved, default-select only areas with pre-emergent
      const hasExistingScope = sprayScope?.mode === 'selected' && sprayScope?.selectedAreaIds?.length > 0
      if (!hasExistingScope && fetched.length > 0) {
        const preEmergentIds = fetched
          .filter(a => a.hasPreEmergent)
          .map(a => String(a._id))
        if (preEmergentIds.length > 0 && preEmergentIds.length < fetched.length) {
          setMode('selected')
          setSelectedIds(new Set(preEmergentIds))
          setDirty(true)
        }
      }
    } catch (err) {
      console.error('Error fetching areas:', err)
    }
    setLoading(false)
  }, [url, scheduleItemId, jobsiteId, sprayScope])

  useEffect(() => { fetchAreas() }, [fetchAreas])

  // Determine if an area is selected
  const isSelected = useCallback((areaId) => {
    const area = areas.find(a => String(a._id) === String(areaId))
    if (mode === 'all') return isSprayableArea(area)
    return isSprayableArea(area) && selectedIds.has(String(areaId))
  }, [areas, mode, selectedIds])

  const handleMapContainerRef = useCallback((node) => {
    mapContainerRef.current = node
    setMapContainerNode(node)
  }, [])

  // Initialize Leaflet map once the container exists after loading
  useEffect(() => {
    if (loading || !mapContainerNode) return

    // Clean up previous map if exists — stop animations first to prevent _leaflet_pos errors
    if (mapInstanceRef.current) {
      try { mapInstanceRef.current.stop() } catch (_) {}
      try { mapInstanceRef.current.remove() } catch (_) {}
      mapInstanceRef.current = null
    }

    // Use jobsite coordinates if available, otherwise default to Phoenix area
    const jobLat = jobsiteLocation?.coordinates?.[1]
    const jobLng = jobsiteLocation?.coordinates?.[0]
    const hasJobsiteCoords = Number.isFinite(jobLat) && Number.isFinite(jobLng) && (jobLat !== 0 || jobLng !== 0)
    const defaultCenter = hasJobsiteCoords ? [jobLat, jobLng] : [33.5, -112.15]

    const map = L.map(mapContainerNode, {
      center: defaultCenter,
      zoom: 17,
      maxZoom: 28,
      zoomControl: true,
    })

    applyBasemapProvider({
      map,
      tileLayerRef,
      provider: googleHybridProvider,
    })

    mapInstanceRef.current = map

    let cancelled = false
    getLatestMaricopaProvider()
      .then((provider) => {
        if (cancelled || !mapInstanceRef.current) return
        applyBasemapProvider({
          map,
          tileLayerRef,
          provider,
        })
      })
      .catch((err) => {
        console.warn('Failed to load latest Maricopa basemap for spray scope:', err)
      })

    // Leaflet needs a kick after the container is laid out
    setTimeout(() => {
      try { map.invalidateSize() } catch (_) {}
    }, 300)

    return () => {
      cancelled = true
      try { map.stop() } catch (_) {}
      if (tileLayerRef.current) {
        try { map.removeLayer(tileLayerRef.current) } catch (_) {}
        tileLayerRef.current = null
      }
      try { map.remove() } catch (_) {}
      mapInstanceRef.current = null
    }
  }, [scheduleItemId, loading, mapContainerNode, jobsiteLocation])

  const activeSlices = useMemo(
    () => slices.filter(s => s.status === 'available' || s.status === 'selected_for_load'),
    [slices]
  )
  const slicedRootAreaIds = useMemo(
    () => new Set(activeSlices.map(s => String(s.rootAreaId))),
    [activeSlices]
  )

  // Draw / update polygons when selection or areas change
  useEffect(() => {
    const map = mapInstanceRef.current
    if (!map) return

    // Clear existing polygons
    polygonLayersRef.current.forEach(layer => map.removeLayer(layer))
    polygonLayersRef.current = []

    const bounds = L.latLngBounds([])

    // Draw area polygons (show ALL areas, not just spray-typed ones)
    areas.forEach(area => {
      if (slicedRootAreaIds.has(String(area._id))) return
      const normalizedPaths = normalizePaths(area.paths)
      if (normalizedPaths.length < 3) return
      const selected = isSelected(area._id)
      const latlngs = normalizedPaths.map(p => [p.lat, p.lng])
      bounds.extend(latlngs)

      const polygon = L.polygon(latlngs, {
        fillColor: selected ? '#FFEB3B' : '#9e9e9e',
        fillOpacity: selected ? 0.55 : 0.15,
        color: selected ? '#F57F17' : '#ffffff',
        weight: selected ? 3.5 : 1.5,
        opacity: selected ? 1 : 0.45,
        dashArray: selected ? null : '4,4',
      }).addTo(map)

      polygon.on('click', () => handleToggleArea(area._id))
      polygonLayersRef.current.push(polygon)
    })

    // Draw custom zones
    customZones.forEach(zone => {
      const normalizedPaths = normalizePaths(zone.paths)
      if (normalizedPaths.length < 3) return
      const latlngs = normalizedPaths.map(p => [p.lat, p.lng])
      bounds.extend(latlngs)

      const polygon = L.polygon(latlngs, {
        fillColor: '#1976d2',
        fillOpacity: 0.25,
        color: '#1565c0',
        weight: 2,
        dashArray: '6,3',
      }).addTo(map)
      polygonLayersRef.current.push(polygon)
    })

    // Draw synced slices (read-only overlays replacing their parent areas)
    activeSlices.forEach(slice => {
      const normalizedPaths = normalizePaths(slice.paths)
      if (normalizedPaths.length < 3) return
      const latlngs = normalizedPaths.map(p => [p.lat, p.lng])
      bounds.extend(latlngs)

      const polygon = L.polygon(latlngs, {
        fillColor: '#CE93D8',
        fillOpacity: 0.4,
        color: '#7B1FA2',
        weight: 2.5,
        opacity: 0.9,
        dashArray: '4,3',
      }).addTo(map)

      polygon.bindTooltip(slice.name || slice.sliceId, {
        sticky: true,
        direction: 'top',
        className: 'slice-tooltip'
      })
      polygonLayersRef.current.push(polygon)
    })

    // Draw property boundary lines
    propertyLines.forEach(line => {
      const normalizedPaths = normalizePaths(line.paths)
      if (normalizedPaths.length < 3) return
      const latlngs = normalizedPaths.map(p => [p.lat, p.lng])
      bounds.extend(latlngs)

      const polyline = L.polygon(latlngs, {
        fill: false,
        color: '#ffffff',
        weight: 3,
        opacity: 0.85,
        dashArray: '8,6',
      }).addTo(map)
      polygonLayersRef.current.push(polyline)
    })

    // Fit bounds if we have any, otherwise center on jobsite
    if (bounds.isValid()) {
      map.invalidateSize()
      map.fitBounds(bounds, { padding: [20, 20] })
    } else {
      const jLat = jobsiteLocation?.coordinates?.[1]
      const jLng = jobsiteLocation?.coordinates?.[0]
      if (Number.isFinite(jLat) && Number.isFinite(jLng) && (jLat !== 0 || jLng !== 0)) {
        map.invalidateSize()
        map.setView([jLat, jLng], 17)
      }
    }
  }, [areas, propertyLines, mode, selectedIds, customZones, isSelected, scheduleItemId, loading, mapContainerNode, jobsiteLocation, activeSlices, slicedRootAreaIds])

  // Compute center from areas for DrawZoneModal
  const mapCenter = useMemo(() => {
    const allPaths = areas.flatMap(a => normalizePaths(a.paths))
    if (allPaths.length) {
      return {
        lat: allPaths.reduce((s, p) => s + p.lat, 0) / allPaths.length,
        lng: allPaths.reduce((s, p) => s + p.lng, 0) / allPaths.length,
      }
    }
    // Fallback to jobsite location for sites with no mapped areas
    const jLat = jobsiteLocation?.coordinates?.[1]
    const jLng = jobsiteLocation?.coordinates?.[0]
    if (Number.isFinite(jLat) && Number.isFinite(jLng) && (jLat !== 0 || jLng !== 0)) {
      return { lat: jLat, lng: jLng }
    }
    return null
  }, [areas, jobsiteLocation])

  // Group areas by spray type for chips — merge 'none' into the closest
  // meaningful bucket so the chip bar stays useful
  const sprayableAreas = useMemo(
    () => areas.filter(isSprayableArea),
    [areas]
  )

  const sprayableIdSet = useMemo(
    () => new Set(sprayableAreas.map(a => String(a._id))),
    [sprayableAreas]
  )

  const areasByType = useMemo(() => {
    const groups = {}
    for (const area of sprayableAreas) {
      // Areas with no recognized spray type: group by pre-emergent status
      let type = area.sprayType || 'other'
      if (type === 'none' || type === 'other') {
        type = area.hasPreEmergent ? 'pre_emergent' : 'no_spray'
      }
      if (!groups[type]) groups[type] = []
      groups[type].push(area)
    }
    return groups
  }, [sprayableAreas])

  // Calculate acreage
  const computeAcres = useCallback((areaList, selectedSet, currentMode) => {
    let total = 0
    for (const area of areaList) {
      if (currentMode === 'all' || selectedSet.has(String(area._id))) {
        const sf = area.areaSF || parseFloat(area.area) || 0
        total += area.acres || parseFloat(area.areaAcres) || (sf ? sf / 43560 : 0)
      }
    }
    for (const zone of customZones) {
      total += zone.areaAcres || 0
    }
    return Math.round(total * 100) / 100
  }, [customZones])

  const totalAcres = computeAcres(sprayableAreas, selectedIds, mode)

  const selectedCount = mode === 'all'
    ? sprayableAreas.length
    : sprayableAreas.filter(a => selectedIds.has(String(a._id))).length

  const sprayableCount = sprayableAreas.length

  // Total SF + acres for all pre-emergent areas (the "defined sprayable" summary)
  const sprayableSummary = useMemo(() => {
    const preEmAreas = sprayableAreas
    let totalSF = 0
    let totalAc = 0
    for (const area of preEmAreas) {
      const sf = area.areaSF || parseFloat(area.area) || 0
      totalSF += sf
      totalAc += area.acres || parseFloat(area.areaAcres) || (sf ? sf / 43560 : 0)
    }
    return { count: preEmAreas.length, totalSF: Math.round(totalSF), totalAc: Math.round(totalAc * 100) / 100 }
  }, [sprayableAreas])

  // Total SF for selected areas (fallback when acres is 0)
  const selectedSF = useMemo(() => {
    let total = 0
    for (const area of sprayableAreas) {
      if (mode === 'all' || selectedIds.has(String(area._id))) {
        total += area.areaSF || parseFloat(area.area) || 0
      }
    }
    return Math.round(total)
  }, [sprayableAreas, mode, selectedIds])

  // Chip handlers
  const handleSelectAll = () => {
    setMode('all')
    setSelectedIds(new Set())
    setDirty(true)
  }

  const handleSelectNone = () => {
    setMode('selected')
    setSelectedIds(new Set())
    setDirty(true)
  }

  const handleSelectByType = (sprayType) => {
    const typeAreaIds = (areasByType[sprayType] || []).map(a => String(a._id))
    const newSelected = new Set(selectedIds)

    // Toggle: if all of this type are selected, deselect them; otherwise select them
    const allSelected = typeAreaIds.every(id => mode === 'all' || newSelected.has(id))
    if (allSelected && mode !== 'all') {
      typeAreaIds.forEach(id => newSelected.delete(id))
    } else {
      // If switching from 'all' mode, start with all areas selected then toggle
      if (mode === 'all') {
        sprayableAreas.forEach(a => newSelected.add(String(a._id)))
      }
      typeAreaIds.forEach(id => newSelected.add(id))
    }

    // Check if all sprayable areas are now selected
    const allSprayable = sprayableAreas
    if (allSprayable.every(a => newSelected.has(String(a._id))) && customZones.length === 0) {
      setMode('all')
      setSelectedIds(new Set())
    } else {
      setMode('selected')
      setSelectedIds(newSelected)
    }
    setDirty(true)
  }

  const handleToggleArea = (areaId) => {
    const area = areas.find(a => String(a._id) === String(areaId))
    if (!isSprayableArea(area)) return

    const id = String(areaId)
    const newSelected = new Set(mode === 'all'
      ? sprayableAreas.map(a => String(a._id))
      : selectedIds
    )

    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }

    // Auto-detect "all selected"
    const allSprayable = sprayableAreas
    if (allSprayable.every(a => newSelected.has(String(a._id))) && customZones.length === 0) {
      setMode('all')
      setSelectedIds(new Set())
    } else {
      setMode('selected')
      setSelectedIds(newSelected)
    }
    setDirty(true)
  }

  const handleAddCustomZone = (zone) => {
    setCustomZones(prev => [...prev, zone])
    if (mode === 'all') {
      setMode('selected')
      setSelectedIds(new Set())
    }
    setDirty(true)
  }

  const handleRemoveCustomZone = (index) => {
    setCustomZones(prev => prev.filter((_, i) => i !== index))
    setDirty(true)
  }

  const handleClearSlices = async () => {
    if (!window.confirm('Clear all area splits? The sub can re-split areas on their next visit.')) return
    setClearingSlices(true)
    try {
      await axios.delete(`${url}/api/herbicide-schedule/${scheduleItemId}/slices`)
      setSlices([])
      fetchAreas()
      setSnackbar({ open: true, message: 'Splits cleared', severity: 'success' })
    } catch (err) {
      console.error('Error clearing slices:', err)
      setSnackbar({ open: true, message: 'Failed to clear splits', severity: 'error' })
    }
    setClearingSlices(false)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const body = {
        mode,
        selectedAreaIds: mode === 'all' ? [] : [...selectedIds].filter(id => sprayableIdSet.has(String(id))),
        customZones,
        totalAcres
      }
      await axios.put(`${url}/api/herbicide-schedule/${scheduleItemId}/spray-scope`, body)
      setDirty(false)
      setExternalScopeUpdate(false)
      spraySnapshotRef.current = {
        mode: body.mode,
        selectedAreaIds: JSON.stringify((body.selectedAreaIds || []).slice().sort()),
        customZonesCount: (body.customZones || []).length
      }
      if (onScopeChange) onScopeChange(body)
      setSnackbar({ open: true, message: 'Scope saved', severity: 'success' })
    } catch (err) {
      console.error('Error saving spray scope:', err)
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' })
    }
    setSaving(false)
  }

  if (loading) {
    return <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
  }

  const noBaseAreas = !areas.length

  const formatSize = (ac, sf) => {
    if (ac > 0) return `${ac} ac`
    if (sf > 0) return sf >= 1000 ? `${Math.round(sf / 1000)}k SF` : `${sf} SF`
    return null
  }

  return (
    <div>
      {/* No base areas message */}
      {noBaseAreas && (
        <Typography variant="body2" style={{ color: '#e65100', fontStyle: 'italic', padding: '4px 0' }}>
          No mapped areas — draw custom zones below{jobsiteId && <> or <Link href={`/site-takeoff-v3/${jobsiteId}`} target="_blank">open SiteTakeoff</Link></>}
        </Typography>
      )}

      {/* Sprayable summary */}
      {sprayableSummary.count > 0 && (
        <Typography variant="caption" style={{ color: '#388E3C', fontWeight: 600, display: 'block', textAlign: 'right', padding: '2px 0' }}>
          {sprayableSummary.count} Sprayable Areas{formatSize(sprayableSummary.totalAc, sprayableSummary.totalSF) ? ` · ${formatSize(sprayableSummary.totalAc, sprayableSummary.totalSF)}` : ''}
        </Typography>
      )}

      {/* Chip bar — area type filters only when base areas exist */}
      <Box display="flex" style={{ gap: 4, padding: '6px 0 6px 0', flexWrap: 'wrap' }} alignItems="center">
        {!noBaseAreas && (
          <>
            <Chip
              size="small"
              label={mode === 'all' ? '✓ All' : 'All'}
              onClick={handleSelectAll}
              style={{
                backgroundColor: mode === 'all' ? '#4caf50' : '#fff',
                color: mode === 'all' ? '#fff' : '#666',
                border: mode === 'all' ? 'none' : '1px solid #ddd',
                fontWeight: mode === 'all' ? 600 : 400,
                cursor: 'pointer'
              }}
            />
            {Object.entries(areasByType).map(([type, typeAreas]) => {
              const typeIds = typeAreas.map(a => String(a._id))
              const allOfTypeSelected = mode === 'all' || typeIds.every(id => selectedIds.has(id))
              return (
                <Chip
                  key={type}
                  size="small"
                  label={`${allOfTypeSelected ? '✓ ' : ''}${SPRAY_TYPE_LABELS[type] || type} (${typeAreas.length})`}
                  onClick={() => handleSelectByType(type)}
                  style={{
                    backgroundColor: allOfTypeSelected ? `${SPRAY_TYPE_COLORS[type]?.fill}30` : '#fff',
                    color: SPRAY_TYPE_COLORS[type]?.stroke || '#666',
                    border: `1px solid ${SPRAY_TYPE_COLORS[type]?.stroke || '#ddd'}40`,
                    fontWeight: allOfTypeSelected ? 600 : 400,
                    cursor: 'pointer'
                  }}
                />
              )
            })}
            <Chip
              size="small"
              label="None"
              onClick={handleSelectNone}
              style={{ backgroundColor: '#f5f5f5', color: '#999', border: '1px solid #e0e0e0', cursor: 'pointer' }}
            />
          </>
        )}
        <Box flex={1} />
        {customZones.length > 0 && (
          <Chip
            size="small"
            label={`${customZones.length} Custom Zone${customZones.length > 1 ? 's' : ''}`}
            style={{ backgroundColor: '#1565c0', color: '#fff', fontWeight: 600 }}
          />
        )}
        <Chip
          size="small"
          label={customZones.length > 0 ? '✏ Draw Another' : '✏ Draw Custom'}
          onClick={() => setDrawModalOpen(true)}
          style={{ backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #bbdefb', cursor: 'pointer' }}
        />
        {activeSlices.length > 0 && (
          <Chip
            size="small"
            label={clearingSlices ? 'Clearing...' : `✕ Clear ${activeSlices.length} Split${activeSlices.length > 1 ? 's' : ''}`}
            onClick={handleClearSlices}
            disabled={clearingSlices}
            style={{ backgroundColor: '#fce4ec', color: '#c62828', border: '1px solid #ef9a9a', cursor: 'pointer' }}
          />
        )}
      </Box>

      {/* Interactive map */}
      <div ref={handleMapContainerRef} style={{ width: '100%', height: 350, borderRadius: 6 }} />

      {/* Summary + Save */}
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ padding: '6px 0' }}>
        <div>
          <Typography variant="caption" style={{ color: '#2e7d32' }}>
            {mode === 'all' ? `All ${sprayableCount} sprayable areas` : `${selectedCount} of ${sprayableCount} sprayable areas`}
            {customZones.length > 0 && ` + ${customZones.length} custom`}
            {activeSlices.length > 0 && ` · ${activeSlices.length} split`}
            {formatSize(totalAcres, selectedSF) ? ` · ${formatSize(totalAcres, selectedSF)}` : ''}
          </Typography>
          {dirty && <Typography variant="caption" style={{ color: '#ff9800', marginLeft: 8 }}>unsaved</Typography>}
          {externalScopeUpdate && <Typography variant="caption" style={{ color: '#e65100', marginLeft: 8 }}>scope updated externally</Typography>}
        </div>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !dirty}
          style={{ fontSize: 11 }}
        >
          {saving ? 'Saving...' : 'Save Scope'}
        </Button>
      </Box>

      {/* Custom zone management */}
      {customZones.length > 0 && (
        <Box style={{ paddingBottom: 4 }}>
          {customZones.map((zone, idx) => (
            <Box key={idx} display="flex" justifyContent="space-between" alignItems="center" style={{ padding: '2px 0' }}>
              <Typography variant="caption" style={{ color: '#1565c0' }}>
                {zone.label || `Custom Zone ${idx + 1}`} — {zone.areaAcres} ac
              </Typography>
              <Typography
                variant="caption"
                style={{ color: '#c62828', cursor: 'pointer' }}
                onClick={() => handleRemoveCustomZone(idx)}
              >
                Remove ×
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Links */}
      <Box style={{ paddingTop: 2 }}>
        {jobsiteId && (
          <Link href={`/site-takeoff-v3/${jobsiteId}`} target="_blank" style={{ fontSize: 10, color: '#1976d2' }}>
            Edit base areas in SiteTakeoff →
          </Link>
        )}
      </Box>

      {/* Draw Zone Modal */}
      {drawModalOpen && (
        <DrawZoneModal
          open={drawModalOpen}
          onClose={() => setDrawModalOpen(false)}
          onAddZone={handleAddCustomZone}
          existingAreas={areas}
          center={mapCenter}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? null : 2000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </div>
  )
}

export default SprayScopeSection
